import bookaMeetingApi from './request';


export type InstagramAccount = {
  userId: string;
  igId: string;
  name: string;
  username: string;
  savedAt: string;
  updatedAt: string;
};

type InstagramAccountsResponse = {
  success?: boolean;
  accounts?: InstagramAccount[];
  account?: InstagramAccount;
  error?: string;
};

export async function updateAiMessageLimit(data: {
  phoneNumberId: string;
  aiUserDailyLimit: number;
  aiTotalDailyLimit: number;
}): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success: boolean;
    message?: string;
    error?: string;
  }>({
    url: '/user/ai-message-limit',
    method: 'PATCH',
    data,
  });
  return response.data;
}

export async function updateUserInstagramAccount(data: {
  phoneNumberId: string;
  userId: string;
  username: string;
  name: string;
}): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success: boolean;
    message?: string;
    error?: string;
  }>({
    url: '/user/instagram-account',
    method: 'PATCH',
    data,
  });
  return response.data;
}

export type InstagramMessageUser = {
  customerId: string;
  customerName: string;
  name?: string;
  username: string;
  channel?: string;
  businessIgId: string;
  lastMessage: string;
  timestamp: string;
  updatedAt: string;
};

export type InstagramThreadMessage = {
  mid: string;
  customerId: string;
  businessIgId: string;
  customerName: string;
  username: string;
  direction: string;
  source: string;
  type: string;
  text: string;
  timestamp: string;
  updatedAt?: string;
};

export async function getInstagramMessageUsers(businessIgId?: string): Promise<InstagramMessageUser[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    users?: InstagramMessageUser[];
  }>({
    url: '/instagram/message-users',
    method: 'GET',
    params: businessIgId ? { businessIgId } : undefined,
  });
  return response.data?.users || [];
}

export async function getInstagramUserMessages(
  customerId: string,
  businessIgId?: string
): Promise<InstagramThreadMessage[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    messages?: InstagramThreadMessage[];
  }>({
    url: '/instagram/user-messages',
    method: 'GET',
    params: {
      customerId,
      ...(businessIgId ? { businessIgId } : {}),
    },
  });
  return response.data?.messages || [];
}

export async function sendInstagramHumanReply(data: {
  recipientId: string;
  text?: string;
  attachment?: { type: 'image' | 'video'; url: string };
  businessIgId?: string;
  humanAgent?: boolean;
}): Promise<{ success?: boolean; messageId?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    messageId?: string;
    error?: string;
  }>({
    url: '/instagram/human-agent',
    method: 'POST',
    data,
  });
  return response.data;
}

export type InstagramComment = {
  mid: string;
  commentId: string;
  customerId: string;
  customerName: string;
  username: string;
  businessIgId: string;
  direction: string;
  source: string;
  text: string;
  timestamp: string;
  isReply: boolean;
};

export async function getInstagramComments(businessIgId?: string): Promise<InstagramComment[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    comments?: InstagramComment[];
  }>({
    url: '/instagram/comments',
    method: 'GET',
    params: businessIgId ? { businessIgId } : undefined,
  });
  return response.data?.comments || [];
}

export async function replyToInstagramComment(data: {
  message: string;
  commentId?: string;
  mediaId?: string;
  businessIgId?: string;
  customerId?: string;
}): Promise<{ success?: boolean; id?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    id?: string;
    error?: string;
  }>({
    url: '/instagram/comment-reply',
    method: 'POST',
    data,
  });
  return response.data;
}

export type InstagramPostComment = {
  id: string;
  text: string;
  username: string;
  timestamp: string;
  replies?: { id: string; text: string; username: string; timestamp: string }[];
};

export type InstagramPost = {
  id: string;
  caption: string;
  mediaType: string;
  imageUrl: string;
  permalink: string;
  timestamp: string;
  likeCount: number | null;
  commentsCount: number | null;
  comments: InstagramPostComment[];
};

export async function getInstagramPosts(businessIgId?: string): Promise<{
  success: boolean;
  posts: InstagramPost[];
  error?: string;
}> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    posts?: InstagramPost[];
    error?: string;
  }>({
    url: '/instagram/posts',
    method: 'GET',
    params: businessIgId ? { businessIgId } : undefined,
  });
  return {
    success: Boolean(response.data?.success),
    posts: response.data?.posts || [],
    error: response.data?.error,
  };
}

export type InstagramPublishType = 'IMAGE' | 'REELS' | 'CAROUSEL' | 'STORIES';

export type InstagramCarouselItem = { type: 'IMAGE' | 'VIDEO'; url: string };

export type InstagramPublishRequest = {
  mediaType?: InstagramPublishType;
  imageUrl?: string;
  videoUrl?: string;
  items?: InstagramCarouselItem[];
  caption?: string;
  altText?: string;
  collaborators?: string[];
  coverUrl?: string;
  thumbOffset?: number;
  shareToFeed?: boolean;
  audioName?: string;
  businessIgId?: string;
  creationId?: string;
  childIds?: string[];
};

export type InstagramPublishResponse = {
  success?: boolean;
  pending?: boolean;
  status?: string;
  creationId?: string;
  childIds?: string[];
  mediaId?: string;
  error?: string;
};

async function publishInstagramStep(data: InstagramPublishRequest): Promise<InstagramPublishResponse> {
  try {
    const response = await bookaMeetingApi.requestWIthAuth<InstagramPublishResponse>({
      url: '/instagram/publish',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (err) {
    const body = (err as { response?: { data?: InstagramPublishResponse } })?.response?.data;
    if (body && typeof body === 'object') return body;
    throw err;
  }
}

/**
 * Publishes an image, reel, carousel or story. Videos keep processing on Instagram's side
 * for a while, so we resume with the returned container ids until it is published.
 */
export async function publishInstagramPost(
  data: InstagramPublishRequest,
  onProgress?: (status: string) => void,
  maxWaitMs = 10 * 60 * 1000
): Promise<InstagramPublishResponse> {
  const startedAt = Date.now();
  let request = { ...data };
  while (true) {
    const result = await publishInstagramStep(request);
    if (!result?.pending) return result;
    if (Date.now() - startedAt > maxWaitMs) {
      return { ...result, success: false, error: 'Instagram is still processing this media. Try again later.' };
    }
    onProgress?.(result.status || 'IN_PROGRESS');
    request = { ...request, creationId: result.creationId, childIds: result.childIds };
    await new Promise((resolve) => setTimeout(resolve, 3000));
  }
}

export type InstagramScheduledStatus = 'scheduled' | 'processing' | 'published' | 'failed';

export type InstagramScheduledPost = {
  id: string;
  businessIgId: string;
  /** ISO date, UTC. */
  scheduledAt: string;
  timeZone?: string;
  status: InstagramScheduledStatus;
  /** True while the worker is publishing it right now. */
  posting?: boolean;
  request: InstagramPublishRequest;
  mediaId?: string;
  error?: string;
  attempts?: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
};

type ScheduledResponse = {
  success?: boolean;
  post?: InstagramScheduledPost;
  posts?: InstagramScheduledPost[];
  error?: string;
};

/** Returns the error body from a 4xx/5xx instead of throwing, so the screen can show its message. */
async function scheduledCall(config: Parameters<typeof bookaMeetingApi.requestWIthAuth>[0]): Promise<ScheduledResponse> {
  try {
    const response = await bookaMeetingApi.requestWIthAuth<ScheduledResponse>(config);
    return response.data || {};
  } catch (err) {
    const body = (err as { response?: { data?: ScheduledResponse } })?.response?.data;
    if (body && typeof body === 'object') return { ...body, success: false };
    return { success: false, error: err instanceof Error ? err.message : 'Request failed.' };
  }
}

export async function getScheduledInstagramPosts(businessIgId?: string) {
  const result = await scheduledCall({
    url: '/instagram/scheduled',
    method: 'GET',
    params: businessIgId ? { businessIgId } : undefined,
  });
  return { success: Boolean(result.success), posts: result.posts || [], error: result.error };
}

export async function scheduleInstagramPost(data: {
  businessIgId?: string;
  scheduledAt: string;
  timeZone?: string;
  request: InstagramPublishRequest;
}) {
  return scheduledCall({ url: '/instagram/scheduled', method: 'POST', data });
}

export async function updateScheduledInstagramPost(
  id: string,
  data: {
    businessIgId?: string;
    scheduledAt?: string;
    timeZone?: string;
    request?: InstagramPublishRequest;
    postNow?: boolean;
  }
) {
  return scheduledCall({ url: `/instagram/scheduled/${encodeURIComponent(id)}`, method: 'PATCH', data });
}

export async function cancelScheduledInstagramPost(id: string, businessIgId?: string) {
  return scheduledCall({
    url: `/instagram/scheduled/${encodeURIComponent(id)}`,
    method: 'DELETE',
    params: businessIgId ? { businessIgId } : undefined,
  });
}

export type InstagramInsight = {
  name?: string;
  title?: string;
  period?: string;
  id?: string;
  description?: string;
  values?: { value?: number | string | Record<string, unknown>; end_time?: string }[];
};

export type InstagramProfileStats = {
  username?: string;
  name?: string;
  followersCount?: number | null;
  followsCount?: number | null;
  mediaCount?: number | null;
};

export async function getInstagramInsights(params: {
  businessIgId?: string;
  mediaId?: string;
}): Promise<{ success: boolean; rows: InstagramInsight[]; profile: InstagramProfileStats | null; reachLast28Days: number | null; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    profile?: InstagramProfileStats | null;
    reachLast28Days?: number | null;
    data?: InstagramInsight[] | { data?: InstagramInsight[] };
    error?: string;
  }>({
    url: '/instagram/insights',
    method: 'GET',
    params: {
      ...(params.businessIgId ? { businessIgId: params.businessIgId } : {}),
      ...(params.mediaId ? { mediaId: params.mediaId } : {}),
    },
  });
  const payload = response.data;
  const raw = payload?.data;
  const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  return {
    success: Boolean(payload?.success),
    rows,
    profile: payload?.profile || null,
    reachLast28Days: typeof payload?.reachLast28Days === 'number' ? payload.reachLast28Days : null,
    error: payload?.error,
  };
}

export async function getInstagramAccounts(): Promise<InstagramAccount[]> {
  const response = await bookaMeetingApi.requestWIthAuth<InstagramAccountsResponse>({
    url: '/instagram/account',
    method: 'GET',
  });
  const data = response.data;
  if (Array.isArray(data?.accounts)) return data.accounts;
  if (data?.account) return [data.account];
  return [];
}


export type InstagramLibraryMedia = {
  key: string;
  kind: 'IMAGE' | 'VIDEO';
  uploadedAt: string;
  url: string;
  /** Instagram only takes JPEG images and MP4/MOV videos. */
  usable: boolean;
};

const IMAGE_FILE = /\.(jpe?g|png|webp|gif)$/i;
const VIDEO_FILE = /\.(mp4|mov|webm|avi|wmv|flv|3gp)$/i;

function toLibraryItem(key: string, url: string, uploadedAt: string): InstagramLibraryMedia | null {
  const path = (key || url).split('?')[0];
  const kind = VIDEO_FILE.test(path) ? 'VIDEO' : IMAGE_FILE.test(path) ? 'IMAGE' : null;
  if (!kind || !url) return null;
  const usable = kind === 'VIDEO' ? /\.(mp4|mov)$/i.test(path) : /\.jpe?g$/i.test(path);
  return { key: key || url, kind, uploadedAt, url, usable };
}

/** The user's files from the Upload Media page (same endpoint that page uses). */
export async function getInstagramMediaLibrary(): Promise<InstagramLibraryMedia[]> {
  const phoneNumberId = sessionStorage.getItem('phoneNumberId');
  if (!phoneNumberId) return [];
  const response = await bookaMeetingApi.requestWIthAuth<{
    data?: { mediaItems?: { s3Key?: string; mediaUrl?: string; createdAt?: string }[] };
  }>({
    url: `/user-media/${phoneNumberId}`,
    method: 'GET',
  });
  const seen = new Set<string>();
  return (response.data?.data?.mediaItems || [])
    .map((m) => toLibraryItem(m.s3Key || '', m.mediaUrl || '', m.createdAt || ''))
    .filter((item): item is InstagramLibraryMedia => Boolean(item))
    .filter((item) => !seen.has(item.url) && seen.add(item.url))
    .sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''));
}

/** Instagram only accepts JPEG images, so convert PNG/WebP/etc. in the browser first. */
async function toJpeg(file: File): Promise<File> {
  if (file.type === 'image/jpeg') return file;
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not convert image to JPEG');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) throw new Error('Could not convert image to JPEG');
  return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
}

/**
 * Uploads a local image/video the same way the Upload Media page does (presigned S3 upload,
 * then saved to /upload-user-media), so it shows up there too. Returns the media.frontdesk24.com link.
 */
export async function uploadInstagramMedia(
  original: File
): Promise<{ success: boolean; url?: string; kind?: 'IMAGE' | 'VIDEO'; error?: string }> {
  const isVideo = original.type.startsWith('video/');
  if (!isVideo && !original.type.startsWith('image/')) {
    return { success: false, error: 'Choose an image or video file.' };
  }
  const phoneNumberId = sessionStorage.getItem('phoneNumberId');
  if (!phoneNumberId) return { success: false, error: 'No phone number selected. Please log in again.' };
  try {
    const file = isVideo ? original : await toJpeg(original);
    const response = await bookaMeetingApi.requestWIthAuth<{
      success?: boolean;
      data?: { uploadUrl?: string; fileKey?: string };
      error?: string;
    }>({
      url: '/whatsapp/file-upload',
      method: 'POST',
      data: {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        folder: 'user-media',
        purpose: 'instagram',
        description: 'User uploaded media file',
      },
    });
    const uploadUrl = response.data?.data?.uploadUrl;
    const fileKey = response.data?.data?.fileKey;
    if (!uploadUrl || !fileKey) {
      return { success: false, error: response.data?.error || 'Could not get an upload link.' };
    }
    const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    if (!put.ok) return { success: false, error: `Upload failed (${put.status}).` };

    const saved = await bookaMeetingApi.requestWIthAuth<{
      success?: boolean;
      data?: { mediaUrl?: string };
      error?: string;
    }>({
      url: `/upload-user-media/${phoneNumberId}`,
      method: 'POST',
      data: { key: fileKey, mediaType: file.type },
    });
    const url = saved.data?.data?.mediaUrl;
    if (!url) return { success: false, error: saved.data?.error || 'Could not save the uploaded file.' };
    return { success: true, url, kind: isVideo ? 'VIDEO' : 'IMAGE' };
  } catch (err) {
    const body = (err as { response?: { data?: { error?: string; maxFileSize?: number } } })?.response?.data;
    if (body?.error === 'File size exceeds limit' && body.maxFileSize) {
      return { success: false, error: `File is too large (max ${Math.round(body.maxFileSize / 1024 / 1024)}MB).` };
    }
    return { success: false, error: body?.error || (err instanceof Error ? err.message : 'Upload failed.') };
  }
}
